import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import { getReaderReadingHistory } from '../services/readerService';
import { getFromStorage, STORAGE_KEYS } from '../utils/storageUtils';
import './BibleProgress.css';

function BibleProgress() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReader, setSelectedReader] = useState('');
  const [readChapters, setReadChapters] = useState([]);
  const [loadingReadHistory, setLoadingReadHistory] = useState(false);
  const [totalChapters, setTotalChapters] = useState(0);
  const [readCount, setReadCount] = useState(0);

  // Fetch all Bible books
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.get('/bible/books');
        setBooks(response.books || []);
        
        // Calculate total chapters
        const total = (response.books || []).reduce((sum, book) => sum + book.chapters, 0);
        setTotalChapters(total);
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch Bible books:', err);
        setError('Failed to load Bible books. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchBooks();
  }, []);

  // Initialize selected reader from localStorage
  useEffect(() => {
    const savedReader = getFromStorage(STORAGE_KEYS.SELECTED_READER);
    if (savedReader) {
      setSelectedReader(savedReader);
    }
  }, []);

  // Listen for reader changes from the ReaderSelector component
  useEffect(() => {
    const handleReaderChange = (event) => {
      const { readerId } = event.detail;
      console.log('BibleProgress - Reader changed event received:', readerId);
      setSelectedReader(readerId);
    };
    
    window.addEventListener('readerChanged', handleReaderChange);
    
    return () => {
      window.removeEventListener('readerChanged', handleReaderChange);
    };
  }, []);

  // Fetch reading history when a reader is selected
  useEffect(() => {
    const fetchReadingHistory = async () => {
      if (!selectedReader) return;
      
      try {
        setLoadingReadHistory(true);
        const response = await getReaderReadingHistory(selectedReader);
        
        // Extract the chapter IDs from the reading history
        const chaptersRead = response.chaptersRead || [];
        setReadChapters(chaptersRead.map(entry => ({
          book: entry.book,
          chapter: entry.chapter,
          chapterId: entry.chapter_id,
          readDate: new Date(entry.read_date || entry.readDate || Date.now())
        })));
        
        setReadCount(chaptersRead.length);
        setLoadingReadHistory(false);
      } catch (err) {
        console.error('Failed to fetch reading history:', err);
        setLoadingReadHistory(false);
      }
    };
    
    fetchReadingHistory();
  }, [selectedReader]);

  // Calculate the age of a read entry in days
  const calculateReadAge = (readDate) => {
    const now = new Date();
    const diffTime = Math.abs(now - readDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Calculate color based on read date (solid green for 30 days, then fading to white over remaining 335 days)
  const calculateReadColor = (readDate) => {
    const age = calculateReadAge(readDate);
    const solidGreenDays = 30; // Stay solid green for 30 days
    const maxAge = 365; // 365 days = 1 year
    
    // If less than 30 days old, use solid green
    if (age <= solidGreenDays) {
      return '#4caf50'; // Solid green
    }
    
    // Calculate how "fresh" the reading is after the solid period
    // 1.0 = just past solid period, 0.0 = read a year ago or more
    const adjustedAge = age - solidGreenDays;
    const remainingFadePeriod = maxAge - solidGreenDays;
    const freshness = Math.max(0, 1 - (adjustedAge / remainingFadePeriod));
    
    // Calculate RGB values for the gradient from green (#4caf50) to white (#ffffff)
    const r = Math.round(76 + (255 - 76) * (1 - freshness));
    const g = Math.round(175 + (255 - 175) * (1 - freshness));
    const b = Math.round(80 + (255 - 80) * (1 - freshness));
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Create a lookup map for read chapters for faster access
  const readChaptersMap = useMemo(() => {
    const map = new Map();
    readChapters.forEach(chapter => {
      const key = `${chapter.book}-${chapter.chapter}`;
      map.set(key, {
        readDate: chapter.readDate,
        color: calculateReadColor(chapter.readDate)
      });
    });
    return map;
  }, [readChapters]);

  // Generate ultra-compact chapter grid for a book
  const renderCompactChapterGrid = (book) => {
    const chapters = Array.from({ length: book.chapters }, (_, i) => i + 1);
    
    return (
      <div className="compact-chapter-grid">
        {chapters.map(chapterNum => {
          const key = `${book.name}-${chapterNum}`;
          const readInfo = readChaptersMap.get(key);
          
          return (
            <div
              key={chapterNum}
              className={`compact-chapter-box ${readInfo ? 'read' : ''}`}
              style={readInfo ? { backgroundColor: readInfo.color } : {}}
            />
          );
        })}
      </div>
    );
  };

  // Group books by testament
  const groupedBooks = useMemo(() => {
    return books.reduce((acc, book) => {
      if (!acc[book.testament]) {
        acc[book.testament] = [];
      }
      acc[book.testament].push(book);
      return acc;
    }, {});
  }, [books]);

  // Calculate reading progress percentage
  const progressPercentage = totalChapters > 0 ? Math.round((readCount / totalChapters) * 100) : 0;

  if (loading) {
    return (
      <div className="text-center p-2">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-1 small">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger small py-1 px-2" role="alert">
        {error}
      </div>
    );
  }

  return (
    <Link to="/bible" className="bible-progress-container">
      {selectedReader && (
        <div className="progress-stats">
          <div className="progress mb-1">
            <div 
              className="progress-bar bg-success" 
              role="progressbar" 
              style={{ width: `${progressPercentage}%` }} 
              aria-valuenow={progressPercentage} 
              aria-valuemin="0" 
              aria-valuemax="100"
            >
              {progressPercentage}%
            </div>
          </div>
          <div className="small text-muted text-center mb-1" style={{ fontSize: '0.7rem' }}>
            {readCount} of {totalChapters} chapters read
          </div>
        </div>
      )}
      
      <div className="bible-progress-content">
        <div className="testament-columns">
          <div className="testament-column">
            <div className="testament-label">OT</div>
            <div className="compact-books-grid">
              {groupedBooks['Old']?.map(book => (
                <div key={book.name} className="compact-book-item">
                  {renderCompactChapterGrid(book)}
                </div>
              ))}
            </div>
          </div>
          
          <div className="testament-column">
            <div className="testament-label">NT</div>
            <div className="compact-books-grid">
              {groupedBooks['New']?.map(book => (
                <div key={book.name} className="compact-book-item">
                  {renderCompactChapterGrid(book)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default BibleProgress; 